import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { projectService } from '../../services/projectService';
import { supabase } from '../../lib/supabase';
import ContractorsVendorsSelector from '../project-management/components/ContractorsVendorsSelector';
import { getProjectContractors, setProjectContractors } from '../../services/contractorVendorLinkService';
import { toDateInputValue } from '../../utils/dateUtils';

const STATUS_OPTIONS = ['Planning', 'Bidding', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];

const ProjectEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [pmUsers, setPmUsers] = useState([]);

  // Contractor/Vendor selection
  const [selectedSCIds, setSelectedSCIds] = useState([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);

  const [form, setForm] = useState({
    projectName: '',
    projectNumber: '',
    customerName: '',
    status: 'Planning',
    totalBudget: '',
    startDate: '',
    endDate: '',
    assignedPmId: '',
    description: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [projectResult, pmResult] = await Promise.all([
        projectService?.getById(id),
        supabase?.from('user_profiles')?.select('id, full_name, first_name, last_name, email')?.eq('is_pm', true)?.order('full_name'),
      ]);

      if (projectResult?.error) {
        setError('Failed to load project.');
      } else {
        const p = projectResult?.data;
        setProject(p);
        setForm({
          projectName: p?.projectName || '',
          projectNumber: p?.projectNumber || '',
          customerName: p?.customerName || '',
          status: p?.status || 'Planning',
          totalBudget: p?.totalBudget != null ? String(p?.totalBudget) : '',
          startDate: toDateInputValue(p?.startDate),
          endDate: toDateInputValue(p?.endDate),
          assignedPmId: p?.assignedPmId || p?.assignedPm?.id || '',
          description: p?.description || '',
        });

        // Load contractor/vendor associations
        try {
          const contractors = await getProjectContractors(id);
          setSelectedSCIds(contractors?.subContractors?.map(c => c?.id) || []);
          setSelectedVendorIds(contractors?.vendors?.map(v => v?.id) || []);
        } catch (err) {
          console.error('Error loading project contractors:', err?.message);
        }
      }

      if (!pmResult?.error && pmResult?.data) {
        setPmUsers(pmResult?.data);
      }

      setLoading(false);
    };

    fetchData();
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContractorChange = ({ subContractorIds, vendorIds }) => {
    setSelectedSCIds(subContractorIds);
    setSelectedVendorIds(vendorIds);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg('');

    const updateData = {
      projectName: form?.projectName,
      customerName: form?.customerName,
      status: form?.status,
      totalBudget: form?.totalBudget !== '' ? parseFloat(form?.totalBudget) : null,
      startDate: form?.startDate || null,
      endDate: form?.endDate || null,
      assignedPmId: form?.assignedPmId || null,
      description: form?.description,
    };

    const { error: saveError } = await projectService?.update(id, updateData);
    if (saveError) {
      setError('Failed to save changes. Please try again.');
    } else {
      // Save contractor/vendor associations
      try {
        await setProjectContractors(id, {
          subContractorIds: selectedSCIds,
          vendorIds: selectedVendorIds,
        });
      } catch (err) {
        console.error('Error saving contractor associations:', err?.message);
      }

      if (form?.status === 'Cancelled') {
        const { error: archiveError } = await projectService?.cancel(id, project?.status);
        if (archiveError) {
          setError('Project saved but could not be archived. Please archive manually.');
        } else {
          navigate('/project-management');
          return;
        }
      } else {
        setSuccessMsg('Project saved successfully.');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    }
    setSaving(false);
  };

  const handleArchive = async () => {
    setArchiving(true);
    const { error: archiveError } = await projectService?.archive(id, project?.status);
    if (archiveError) {
      setError('Failed to archive project.');
      setArchiving(false);
    } else {
      navigate('/project-management');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span>Loading project...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!project && !loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="AlertCircle" size={40} className="mx-auto mb-3 text-destructive" />
            <p className="text-foreground font-medium">Project not found.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/project-management')}>
              Back to Projects
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/project-management')}
            className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="ArrowLeft" size={18} />
          </button>
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Edit Project</p>
            <h1 className="text-xl font-bold text-foreground leading-tight">
              #{form?.projectNumber}
            </h1>
          </div>
        </div>

        {/* Feedback messages */}
        {error && (
          <div className="mb-4 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm border border-destructive/20">
            <Icon name="AlertCircle" size={16} />
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 flex items-center gap-2 p-3 rounded-md bg-green-500/10 text-green-700 dark:text-green-400 text-sm border border-green-500/20">
            <Icon name="CheckCircle" size={16} />
            {successMsg}
          </div>
        )}

        {/* Form Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-5">
          <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide border-b border-border pb-3">
            Project Information
          </h2>

          {/* Project Number (read-only) */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Project Number
            </label>
            <input
              type="text"
              value={form?.projectNumber}
              readOnly
              className="w-full px-3 py-2 rounded-md border border-border bg-muted text-muted-foreground text-sm cursor-not-allowed"
            />
          </div>

          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Project Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={form?.projectName}
              onChange={(e) => handleChange('projectName', e?.target?.value)}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            />
          </div>

          {/* Customer */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Customer
            </label>
            <input
              type="text"
              value={form?.customerName}
              onChange={(e) => handleChange('customerName', e?.target?.value)}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Status
            </label>
            <select
              value={form?.status}
              onChange={(e) => handleChange('status', e?.target?.value)}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            >
              {STATUS_OPTIONS?.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Budget */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Total Budget ($)
            </label>
            <input
              type="number"
              value={form?.totalBudget}
              onChange={(e) => handleChange('totalBudget', e?.target?.value)}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            />
          </div>

          {/* Start Date / End Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                value={form?.startDate}
                onChange={(e) => handleChange('startDate', e?.target?.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                End Date
              </label>
              <input
                type="date"
                value={form?.endDate}
                onChange={(e) => handleChange('endDate', e?.target?.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Assigned PM */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Assigned Project Manager
            </label>
            <select
              value={form?.assignedPmId}
              onChange={(e) => handleChange('assignedPmId', e?.target?.value)}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            >
              <option value="">— Unassigned —</option>
              {pmUsers?.map((pm) => (
                <option key={pm?.id} value={pm?.id}>
                  {pm?.full_name || pm?.email}
                </option>
              ))}
            </select>
          </div>

          {/* Contractors & Vendors */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Contractors &amp; Vendors
            </label>
            <ContractorsVendorsSelector
              selectedSCIds={selectedSCIds}
              selectedVendorIds={selectedVendorIds}
              onChange={handleContractorChange}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Description
            </label>
            <textarea
              value={form?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex items-center justify-between gap-3">
          {/* Archive */}
          <Button
            variant="outline"
            onClick={() => setShowArchiveConfirm(true)}
            iconName="Archive"
            iconPosition="left"
            className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
          >
            Archive Project
          </Button>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/project-management')}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
              iconName="Save"
              iconPosition="left"
            >
              Save Changes
            </Button>
          </div>
        </div>

        {/* Archive Confirmation */}
        {showArchiveConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-card border border-border rounded-lg shadow-xl p-6 max-w-sm w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <Icon name="Archive" size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Archive Project?</h3>
                  <p className="text-sm text-muted-foreground">This project will be hidden from the main list.</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                <strong className="text-foreground">#{form?.projectNumber}</strong> — {form?.projectName} will be moved to the Archive. You can restore it at any time from the Archive section.
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowArchiveConfirm(false)} disabled={archiving}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleArchive}
                  loading={archiving}
                  className="bg-amber-600 hover:bg-amber-700 border-amber-600"
                >
                  Archive
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ProjectEdit;
